import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import Layout from '../components/Layout';
import { useNavigate, useParams, useLocation } from 'react-router-dom';


const MyAssignments = () => {
     const location = useLocation();
    const { conversationId } = useParams();
    const navigate = useNavigate();
    
 
    console.log('=== MyAssignments Component Loaded ===');
    console.log('conversationId from URL:', conversationId);
    console.log('Current URL:', window.location.href);

    
    
    
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [botPaused, setBotPaused] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [cannedResponses, setCannedResponses] = useState([]);
    const [showCannedResponses, setShowCannedResponses] = useState(false);
    const [filteredCannedResponses, setFilteredCannedResponses] = useState([]);
    const [socketConnected, setSocketConnected] = useState(false);
    const [lastMessageIds, setLastMessageIds] = useState({}); // Track last message ID per conversation
    
    const messageInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const socketRef = useRef(null);
    const messagePollingInterval = useRef(null);
    const messagePollingRef = useRef(null);
    const MESSAGES_POLL_INTERVAL = 2000; // Poll every 2 seconds for new messages

    // Define pollForNewMessages using useCallback BEFORE any conditional returns
    const pollForNewMessages = useCallback(async () => {
        if (!selectedConversation) return;
        
        try {
            const token = localStorage.getItem('token');
            const conversationId = selectedConversation.conversation_id || selectedConversation.id;
            const lastMessageId = lastMessageIds[conversationId] || 0;
            
            console.log(`Polling conversation ${conversationId}, last message ID: ${lastMessageId}`);
            
            // Only fetch messages newer than what we have
            const response = await fetch(
                `/api/v1/conversations/${conversationId}/messages?after_id=0`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                
                // FIX: Check if data.data exists AND is an array
                if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
                    console.log(`Found ${data.data.length} new messages for conversation ${conversationId}`);
                    
                    // Add new messages to existing messages
                    setMessages(prevMessages => {
                        // Create a Set of existing message IDs to avoid duplicates
                        const existingIds = new Set(prevMessages.map(m => m.id));
                        
                        // Filter out duplicates
                        const newMessages = data.data.filter(msg => !existingIds.has(msg.id));
                        
                        if (newMessages.length > 0) {
                            // Update the last message ID for this conversation
                            const maxId = Math.max(...newMessages.map(m => m.id));
                            setLastMessageIds(prev => ({
                                ...prev,
                                [conversationId]: maxId
                            }));
                            
                            // Combine and sort messages
                            const allMessages = [...prevMessages, ...newMessages].sort((a, b) => 
                                new Date(a.timestamp) - new Date(b.timestamp)
                            );
                            
                            // Auto-scroll to bottom if we got new messages
                            setTimeout(() => {
                                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }, 100);
                            
                            return allMessages;
                        }
                        
                        return prevMessages;
                    });
                    
                    // Update unread count to 0 for this conversation in assignments
                    setAssignments(prev => prev.map(a => 
                        a.conversation_id === conversationId ? { ...a, unread_count: 0 } : a
                    ));
                }
            } else {
                console.error('Failed to poll messages:', response.status);
            }
        } catch (error) {
            console.error('Error polling for new messages:', error);
        }
    }, [selectedConversation, lastMessageIds]);

    // Define pollAssignmentsForUnread using useCallback
    const pollAssignmentsForUnread = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/v1/conversations?status=active', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // FIX: Check if data.data exists AND is an array
                if (data.success && data.data && Array.isArray(data.data)) {
                    // Update unread counts for all conversations
                    setAssignments(prevAssignments => {
                        return prevAssignments.map(assignment => {
                            const updated = data.data.find(c => c.id === assignment.conversation_id);
                            if (updated) {
                                // Don't update unread for selected conversation
                                const isSelected = selectedConversation?.conversation_id === assignment.conversation_id;
                                return {
                                    ...assignment,
                                    unread_count: isSelected ? 0 : (updated.unread_count || 0),
                                    last_activity: updated.last_activity,
                                    total_messages: updated.total_messages
                                };
                            }
                            return assignment;
                        });
                    });
                } else {
                    console.warn('Invalid data structure from /api/v1/conversations:', data);
                }
            } else {
                console.error('Failed to fetch conversations:', response.status);
            }
        } catch (error) {
            console.error('Error polling assignments:', error);
        }
    }, [selectedConversation]);

    // Initialize WebSocket connection
    useEffect(() => {
        const token = localStorage.getItem('token');
        
        // FIX: Use the correct URL - detect if local development or production
        const socketUrl = window.location.hostname === 'localhost' 
            ? 'http://localhost:3001' 
            : 'https://agents.matix.one';
        
        console.log('Connecting to WebSocket server at:', socketUrl);
        
        // Connect to WebSocket server
        socketRef.current = io(socketUrl, {
            auth: {
                token: token
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5
        });

        // Socket event listeners
        socketRef.current.on('connect', () => {
            console.log('✅ Connected to WebSocket server');
            setSocketConnected(true);
            
            // IMPORTANT: Stop any polling when WebSocket connects
            if (messagePollingInterval.current) {
                clearInterval(messagePollingInterval.current);
                messagePollingInterval.current = null;
                console.log('Stopped message polling - using WebSocket');
            }
        });

        socketRef.current.on('disconnect', (reason) => {
            console.log('❌ Disconnected from WebSocket:', reason);
            setSocketConnected(false);
            
            // Optionally restart polling as fallback
            // startMessagePolling();
        });

        socketRef.current.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error.message);
            setSocketConnected(false);
        });

        socketRef.current.on('new-message', (data) => {
            console.log('📩 New message received:', data);
            // Update messages if this conversation is selected
            if (selectedConversation && data.conversation_id === selectedConversation.conversation_id) {
                setMessages(prev => {
                    // Avoid duplicates
                    const exists = prev.some(msg => msg.id === data.message.id);
                    if (exists) return prev;
                    return [...prev, data.message];
                });
                scrollToBottom();
            }
            // Update assignment list to show new message count
            updateAssignmentMessageCount(data.conversation_id);
        });

        socketRef.current.on('conversation-assigned', (data) => {
            console.log('📋 Conversation assigned:', data);
            fetchMyAssignments();
        });

        socketRef.current.on('conversation-completed', (data) => {
            console.log('✅ Conversation completed:', data);
            if (selectedConversation && data.conversation_id === selectedConversation.conversation_id) {
                setSelectedConversation(null);
                setMessages([]);
            }
            fetchMyAssignments();
        });

        // Server confirmation event
        socketRef.current.on('connected', (data) => {
            console.log('🔌 Server confirmation:', data);
        });

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
            if (messagePollingInterval.current) {
                clearInterval(messagePollingInterval.current);
            }
        };
    }, []);

    // Join conversation room when selected
    useEffect(() => {
        if (selectedConversation && socketRef.current && socketConnected) {
            console.log('Joining conversation room:', selectedConversation.conversation_id);
            socketRef.current.emit('join-conversation', selectedConversation.conversation_id, (response) => {
                console.log('Join response:', response);
            });
            
            return () => {
                console.log('Leaving conversation room:', selectedConversation.conversation_id);
                socketRef.current.emit('leave-conversation', selectedConversation.conversation_id);
            };
        }
    }, [selectedConversation, socketConnected]);

    useEffect(() => {
        fetchMyAssignments();
        fetchCannedResponses();
    }, []);

    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(selectedConversation.conversation_id);
            
            // Only use polling as fallback if WebSocket is not connected
            if (!socketConnected) {
                console.log('WebSocket not connected, using polling fallback');
                startMessagePolling();
            } else {
                stopMessagePolling();
            }
        } else {
            stopMessagePolling();
        }
        
        return () => {
            stopMessagePolling();
        };
    }, [selectedConversation, socketConnected]);

    // Polling for new messages
    useEffect(() => {
        // Clear any existing polling interval
        if (messagePollingRef.current) {
            clearInterval(messagePollingRef.current);
        }
        
        // Start polling if we have a selected conversation
        if (selectedConversation) {
            // Poll immediately
            pollForNewMessages();
            
            // Then set up interval for continuous polling
            messagePollingRef.current = setInterval(pollForNewMessages, MESSAGES_POLL_INTERVAL);
            
            console.log(`Started polling for messages in conversation ${selectedConversation.conversation_id}`);
        }
        
        // Cleanup on unmount or when conversation changes
        return () => {
            if (messagePollingRef.current) {
                clearInterval(messagePollingRef.current);
                console.log('Stopped message polling');
            }
        };
    }, [selectedConversation, pollForNewMessages]);

    // Polling for assignment updates
    useEffect(() => {
        const interval = setInterval(pollAssignmentsForUnread, 5000);
        return () => clearInterval(interval);
    }, [pollAssignmentsForUnread]);

useEffect(() => {
    // ✅ NEW: Check URL query parameter FIRST
    const params = new URLSearchParams(window.location.search);
    const conversationIdFromQuery = params.get('conversation');
    
    // Then check navigation state and URL param
    const stateConvId = location.state?.openConversationId;
    const convId = conversationIdFromQuery || stateConvId || conversationId;
    
    console.log('🔍 Checking for conversation to open...');
    console.log('📍 From URL query:', conversationIdFromQuery);
    console.log('📍 From state:', stateConvId);
    console.log('📍 From URL param:', conversationId);
    console.log('📋 Assignments loaded:', assignments.length);
    
    
    
// ✅ ONLY run if we have a conversation ID and it's NOT already selected
if (convId && !selectedConversation) {
    // Try to find in assignments first
    const conversation = assignments.find(a => a.id === parseInt(convId));
    
    if (conversation) {
        console.log('✅ Found in assignments, opening...');
        handleConversationClick(conversation);
        
        // Clear URL query parameter after opening
        if (conversationIdFromQuery) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        // Clear navigation state after opening
        if (stateConvId) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    } else if (assignments.length > 0) {
        console.log('⚠️ Not in assignments, fetching from API...');
        fetchConversationById(convId);
        
        // Clear URL query parameter after fetching
        if (conversationIdFromQuery) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    } else {
        console.log('⏳ Waiting for assignments to load...');
    }
}
},
[location.state, conversationId, assignments.length]);  // ✅ Changed: assignments.length instead of assignments


// Function to fetch specific conversation if not in assignments
const fetchConversationById = async (convId) => {
    const token = localStorage.getItem('token');
    try {
        const response = await fetch(`/api/v1/conversations/${convId}`, {  // ✅ FIXED: Added parentheses
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
                console.log('✅ Conversation fetched from API:', data.data);
                const normalizedConv = {
                    ...data.data,
                    conversation_id: data.data.id
                };
                setSelectedConversation(normalizedConv);
                fetchMessages(data.data.id);
            }
        } else {
            console.error('❌ Failed to fetch conversation:', response.status);
        }
    } catch (err) {
        console.error('❌ Error fetching conversation:', err);
    }
};

    // Polling functions (as fallback)
    const startMessagePolling = () => {
        if (messagePollingInterval.current) return;
        
        console.log('Starting message polling (fallback mode)');
        messagePollingInterval.current = setInterval(() => {
            if (selectedConversation) {
                fetchMessages(selectedConversation.conversation_id, true); // silent fetch
            }
        }, 5000); // Poll every 5 seconds instead of continuously
    };

    const stopMessagePolling = () => {
        if (messagePollingInterval.current) {
            clearInterval(messagePollingInterval.current);
            messagePollingInterval.current = null;
            console.log('Stopped message polling');
        }
    };

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Handle canned response shortcuts
    useEffect(() => {
        if (newMessage.startsWith('/')) {
            const shortcut = newMessage.substring(1);
            if (shortcut.length > 0) {
                const filtered = cannedResponses.filter(r => 
                    r.shortcuts && r.shortcuts.toLowerCase().startsWith(shortcut.toLowerCase())
                );
                setFilteredCannedResponses(filtered);
                setShowCannedResponses(filtered.length > 0);
            } else {
                setShowCannedResponses(false);
            }
        } else {
            setShowCannedResponses(false);
        }
    }, [newMessage, cannedResponses]);

    const updateAssignmentMessageCount = (conversationId) => {
        setAssignments(prev => prev.map(assignment => {
            if (assignment.conversation_id === conversationId) {
                return { ...assignment, total_messages: (assignment.total_messages || 0) + 1 };
            }
            return assignment;
        }));
    };

    const fetchCannedResponses = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/api/v1/canned-responses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                // FIX: Check if data.data exists AND is an array
                if (data.success && data.data && Array.isArray(data.data)) {
                    setCannedResponses(data.data);
                } else {
                    console.warn('Invalid canned responses data structure:', data);
                    setCannedResponses([]);
                }
            } else {
                console.error('Failed to fetch canned responses:', response.status);
            }
        } catch (err) {
            console.error('Error fetching canned responses:', err);
        }
    };

    const fetchMyAssignments = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await fetch('/api/v1/conversations/my-assignments', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                // FIX: Check if data.data exists AND is an array
                if (data.success && data.data && Array.isArray(data.data)) {
                    setAssignments(data.data);
                    setError(null);
                } else {
                    console.warn('Invalid assignments data structure:', data);
                    setAssignments([]);
                }
            } else if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                setError('Failed to load assignments');
            }
        } catch (err) {
            console.error('Error fetching assignments:', err);
            setError('Network error loading assignments');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (conversationId, silent = false) => {
        const token = localStorage.getItem('token');
        
        if (!silent) {
            setLoadingMessages(true);
            setMessages([]); // Clear previous messages
        }

        try {
            const response = await fetch(`/api/v1/conversations/${conversationId}/messages?limit=100`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                
                // FIX: Check if data.data exists AND is an array
                if (data.success && data.data && Array.isArray(data.data)) {
                    if (silent) {
                        // For polling, only update if there are new messages
                        setMessages(prev => {
                            if (data.data.length > prev.length) {
                                console.log('New messages detected via polling');
                                return data.data;
                            }
                            return prev;
                        });
                    } else {
                        setMessages(data.data);
                        if (data.data.length > 0) {
                            // Track the last message ID for this conversation
                            const maxId = Math.max(...data.data.map(m => m.id));
                            setLastMessageIds(prev => ({
                                ...prev,
                                [conversationId]: maxId
                            }));
                        }
                    }
                    
                    // Emit to socket that we're viewing this conversation
                    if (socketRef.current && socketConnected) {
                        socketRef.current.emit('viewing-conversation', conversationId);
                    }
                } else {
                    console.warn('Invalid messages data structure:', data);
                    if (!silent) setMessages([]);
                }
            } else {
                if (!silent) {
                    const errorData = await response.json();
                    showNotification(errorData.message || 'Failed to load messages', 'error');
                }
            }
        } catch (err) {
            if (!silent) {
                console.error('Error fetching messages:', err);
                showNotification('Failed to load messages', 'error');
            }
        } finally {
            if (!silent) setLoadingMessages(false);
        }
    };

   const handleConversationClick = (assignment) => {
    console.log('Selected conversation:', assignment);
    
    // إضافة conversation_id عشان يشتغل مع باقي الكود
    const normalizedAssignment = {
        ...assignment,
    conversation_id: assignment.conversation_id || assignment.id
    };
    
    setSelectedConversation(normalizedAssignment);
    // Fetch real bot_paused status from server
    const token = localStorage.getItem("token");
    fetch(`/api/v1/conversations/${normalizedAssignment.conversation_id}/bot-status`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
        if (d.success) setBotPaused(d.bot_paused);
    }).catch(() => setBotPaused(false));
    setMessages([]);
    };
    
    const insertCannedResponse = async (response) => {
        const content = selectedConversation?.language === 'ar' ? 
            (response.content_ar || response.content_en) : 
            (response.content_en || response.content_ar);
        
        setNewMessage(content);
        setShowCannedResponses(false);
        
        // Track usage
        const token = localStorage.getItem('token');
        try {
            await fetch(`/api/v1/canned-responses/${response.id}/use`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        } catch (err) {
            console.error('Error tracking canned response usage:', err);
        }
        
        messageInputRef.current?.focus();
    };

const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) {
        return;
    }

    const token = localStorage.getItem('token');
    const messageText = newMessage.trim();
    setSendingMessage(true);
    setNewMessage(''); // Clear input immediately for better UX

    try {
        const conversationId = selectedConversation.conversation_id || selectedConversation.id;
        
        console.log('Sending message to conversation:', conversationId);
        
        const response = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                message: messageText,
                message_type: 'agent'
            })
        });

        const data = await response.json();
        console.log('Send message response:', data);

        if (response.ok && data.success) {
            // FIXED: Use the actual message from the server response
            // The backend returns the saved message with the real database ID
            if (data.data) {
                const serverMessage = data.data;
                
                setMessages(prev => {
                    // Check if message already exists (avoid duplicates)
                    const exists = prev.some(m => m.id === serverMessage.id);
                    if (!exists) {
                        const updated = [...prev, serverMessage].sort((a, b) => 
                            new Date(a.timestamp) - new Date(b.timestamp)
                        );
                        
                        // Update last message ID
                        setLastMessageIds(prevIds => ({
                            ...prevIds,
                            [conversationId]: serverMessage.id
                        }));
                        
                        return updated;
                    }
                    return prev;
                });
                
                // Emit message sent event if WebSocket is connected
                if (socketRef.current && socketConnected) {
                    socketRef.current.emit('message-sent', {
                        conversation_id: conversationId,
                        message: serverMessage
                    });
                }
            }
            
            // Check if WhatsApp was sent successfully
            if (data.whatsapp_sent) {
                showNotification('Message sent successfully to customer!', 'success');
            } else if (data.whatsapp_error) {
                showNotification(`Message saved but WhatsApp failed: ${data.whatsapp_error}`, 'warning');
            } else {
                showNotification('Message sent successfully!', 'success');
            }
            
            scrollToBottom();
            setBotPaused(true);
        } else {
            // If failed, restore the message in input
            setNewMessage(messageText);
            showNotification(data.message || 'Failed to send message', 'error');
        }
    } catch (err) {
        console.error('Error sending message:', err);
        // Restore message on network error
        setNewMessage(messageText);
        showNotification('Network error sending message', 'error');
    } finally {
        setSendingMessage(false);
    }
};

    const toggleBot = async () => {
        if (!selectedConversation) return;
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/api/v1/conversations/${selectedConversation.conversation_id}/toggle-bot`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
            });
            const data = await response.json();
            if (data.success) {
                setBotPaused(data.bot_paused);
                showNotification(data.bot_paused ? "Bot paused" : "Bot resumed", "success");
                setSelectedConversation(prev => ({...prev, bot_paused: data.bot_paused ? 1 : 0}));
                setAssignments(prev => prev.map(a => a.conversation_id === selectedConversation.conversation_id ? {...a, bot_paused: data.bot_paused ? 1 : 0} : a));
            }
        } catch (error) {
            console.error("Error toggling bot:", error);
            showNotification("Failed to toggle bot", "error");
        }
    };

    const completeConversation = async () => {
        if (!selectedConversation) return;

        const token = localStorage.getItem('token');
        if (!window.confirm('Are you sure you want to mark this conversation as completed?')) {
            return;
        }

        try {
            const response = await fetch(`/api/v1/conversations/${selectedConversation.conversation_id}/complete`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                showNotification('Conversation marked as completed', 'success');
                
                // Emit completion event if WebSocket is connected
                if (socketRef.current && socketConnected) {
                    socketRef.current.emit('conversation-completed', {
                        conversation_id: selectedConversation.conversation_id
                    });
                }
                
                setSelectedConversation(null);
                setMessages([]);
                fetchMyAssignments();
            } else {
                showNotification(data.message || 'Failed to complete conversation', 'error');
            }
        } catch (err) {
            console.error('Error completing conversation:', err);
            showNotification('Network error completing conversation', 'error');
        }
    };

    const showNotification = (message, type = 'info') => {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 15px 20px;
            border-radius: 5px;
color: ${type === 'warning' ? '#000' : 'white'};
font-weight: bold;
            z-index: 1000;
            max-width: 300px;
background-color: ${type === 'success' ? '#28a745' : type === 'error' ? '#dc3545' : type === 'warning' ? '#ffc107' : '#007bff'};

            animation: slideIn 0.3s ease-out;
        `;
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            if (document.body.contains(notification)) {
                notification.style.animation = 'slideOut 0.3s ease-in';
                setTimeout(() => {
                    if (document.body.contains(notification)) {
                        document.body.removeChild(notification);
                    }
                }, 300);
            }
        }, 3000);
    };

    const getTimeSince = (timestamp) => {
        const now = new Date();
        const then = new Date(timestamp);
        const minutes = Math.floor((now - then) / 60000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        return `${Math.floor(hours / 24)}d ago`;
    };

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('ar-EG', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
};

    if (loading) {
        return (
            <Layout>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h3>Loading your assignments...</h3>
                </div>
            </Layout>
        );
    }

// ابحث عن return في MyAssignments.js (حوالي سطر 795)
// واستبدل كل الـ JSX بهذا الكود:

return (
    <Layout>
        <div style={{ minHeight: '100vh', backgroundColor: '#0a0e27' }}>
            {/* Header */}
            <div style={{ 
                backgroundColor: 'rgba(26, 32, 56, 0.8)', 
                padding: '15px 20px', 
                marginBottom: '20px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div style={{ 
                    maxWidth: '1400px', 
                    margin: '0 auto', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px'
                }}>
                    <h1 style={{ 
                        margin: 0, 
                        color: '#f9fafb',
                        fontSize: 'clamp(18px, 5vw, 24px)'
                    }}>
                        My Assignments
                    </h1>
                    
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        flexWrap: 'wrap'
                    }}>
                        {/* WebSocket Status */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '5px 10px',
                            backgroundColor: socketConnected ? '#d4edda' : '#f8d7da',
                            borderRadius: '15px',
                            fontSize: '11px'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: socketConnected ? '#28a745' : '#dc3545',
                                animation: socketConnected ? 'pulse 2s infinite' : 'none'
                            }}></div>
                            <span style={{ color: socketConnected ? '#155724' : '#721c24' }}>
                                {socketConnected ? 'Live' : 'Offline'}
                            </span>
                        </div>
                        
                        <button
                            onClick={fetchMyAssignments}
                            style={{
                                padding: '8px 15px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Refresh
                        </button>
                        
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={{
                                padding: '8px 15px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Dashboard
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ 
                maxWidth: '1400px', 
                margin: '0 auto', 
                padding: '0 10px'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: selectedConversation ? '1fr' : '1fr',
                    gap: '20px',
                    minHeight: 'calc(100vh - 150px)'
                }}>
                    {/* Assignments List - يختفي على الموبايل لما في محادثة مفتوحة */}
                    <div style={{
                        display: selectedConversation ? 'none' : 'block',
                        backgroundColor: 'rgba(26, 32, 56, 0.8)',
                        borderRadius: '10px',
                        padding: '15px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 180px)'
                    }} 
                    className="assignments-list">
                        <h2 style={{ 
                            marginTop: 0, 
                            marginBottom: '15px',
                            fontSize: 'clamp(16px, 4vw, 20px)',
                            color: '#f9fafb'
                        }}>
                            Active Assignments ({assignments.length})
                        </h2>

                        {assignments.length === 0 ? (
                            <div style={{ 
                                textAlign: 'center', 
                                color: '#9ca3af', 
                                padding: '40px 20px' 
                            }}>
                                <h4>No active assignments</h4>
                                <p>You don't have any conversations assigned to you.</p>
                                <button
                                    onClick={() => navigate('/queue')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Go to Message Queue
                                </button>
                            </div>
                        ) : (
                            assignments.map(assignment => (
                                <div
                                    key={assignment.id}
                                    onClick={() => handleConversationClick(assignment)}
                                    style={{
                                        padding: '12px',
                                        marginBottom: '10px',
                                        backgroundColor: 'transparent',
                                        borderRadius: '8px',
                                        borderLeft: selectedConversation?.conversation_id === assignment.conversation_id 
                                            ? '4px solid #6366f1'
                                            : '4px solid #3b82f6',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid rgba(99, 102, 241, 0.3)'
                                    }}
                                >
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'start',
                                        gap: '10px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flex: 1, minWidth: '200px' }}>
                                            <h4 style={{ 
                                                margin: '0 0 8px 0', 
                                                color: '#f9fafb',
                                                fontSize: '15px',
                                                fontWeight: 'bold'
                                            }}>
                                                {assignment.phone_number || assignment.session_id}
                                            </h4>
                                            
                                            <div style={{ 
                                                display: 'flex', 
                                                gap: '10px', 
                                                fontSize: '11px', 
                                                color: '#9ca3af',
                                                flexWrap: 'wrap'
                                            }}>
                                                <span>{assignment.language === 'ar' ? 'Arabic' : 'English'}</span>
                                                <span>{assignment.total_messages} messages</span>
                                                <span>Assigned: {getTimeSince(assignment.assigned_at)}</span>
                                            </div>
                                            
                                            <div style={{ 
                                                fontSize: '11px', 
                                                color: '#6b7280', 
                                                marginTop: '5px' 
                                            }}>
                                                Last activity: {getTimeSince(assignment.last_message_time || assignment.last_activity)}
                                            </div>
                                        </div>
                                        
                                        <span style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            Active
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Conversation View - Full screen على الموبايل */}
                    {selectedConversation && (
                        <div style={{
                            backgroundColor: 'rgba(26, 32, 56, 0.8)',
                            borderRadius: '10px',
                            padding: '15px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: 'calc(100vh - 180px)',
                            position: 'relative'
                        }}
                        className="conversation-view">
                            {/* Back Button للموبايل */}
                            <button
                                onClick={() => setSelectedConversation(null)}
                                style={{
                                    display: 'none',
                                    padding: '8px 12px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    marginBottom: '10px',
                                    fontSize: '13px'
                                }}
                                className="mobile-back-button"
                            >
                                ← Back to List
                            </button>

                            {/* Conversation Header */}
                            <div style={{
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                paddingBottom: '12px',
                                marginBottom: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '10px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div>
                                        <h3 style={{ 
                                            margin: 0, 
                                            color: '#f9fafb',
                                            fontSize: 'clamp(16px, 4vw, 18px)'
                                        }}>
                                            {selectedConversation.phone_number || selectedConversation.session_id}
                                        </h3>
                                        {selectedConversation.user_name && (
                                            <p style={{ 
                                                margin: '5px 0 0 0', 
                                                color: '#9ca3af',
                                                fontSize: '13px'
                                            }}>
                                                {selectedConversation.user_name}
                                            </p>
                                        )}
                                    </div>
                                    
                                    <button
                                        onClick={toggleBot}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: botPaused ? "#dc3545" : "#28a745",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            marginRight: "8px"
                                        }}
                                    >
                                        {botPaused ? "🤖 Resume Bot" : "⏸️ Pause Bot"}
                                    </button>
                                    <button
                                        onClick={completeConversation}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Complete
                                    </button>
                                </div>
                            </div>

                            {/* Messages Container */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '15px',
                                backgroundColor: '#0a0e27',
                                borderRadius: '8px',
                                marginBottom: '10px'
                            }}>
                                {loadingMessages ? (
                                    <div style={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        padding: '40px', 
                                        color: '#6b7280' 
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            border: '3px solid rgba(99, 102, 241, 0.1)',
                                            borderTopColor: '#6366f1',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        <p style={{ marginTop: '15px' }}>Loading messages...</p>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '60px 20px', 
                                        color: '#6b7280' 
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>💬</div>
                                        <p style={{ fontSize: '14px', color: '#9ca3af' }}>No messages yet</p>
                                    </div>
                                ) : (
                                    <>
                                        {messages.map((message, index) => {
                                            const isAgent = message.message_type === 'agent';
                                            const isAI = message.message_type === 'ai';
                                            
                                            return (
                                                <div 
                                                    key={message.id || index}
                                                    className={`message-item ${isAgent ? 'agent' : isAI ? 'ai' : 'customer'}`}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: isAgent ? 'flex-end' : 'flex-start',
                                                        marginBottom: '12px'
                                                    }}
                                                >
                                                    <div 
                                                        className="message-bubble"
                                                        style={{
                                                            maxWidth: '85%',
                                                            padding: '10px 14px',
                                                            borderRadius: '12px',
                                                            borderBottomLeftRadius: isAgent ? '12px' : '2px',
                                                            borderBottomRightRadius: isAgent ? '2px' : '12px',
                                                            backgroundColor: isAgent ? '#25d366' : isAI ? '#e8e8e8' : '#ffffff',
                                                            color: isAgent ? '#ffffff' : '#000000',
                                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <div style={{
                                                            fontSize: '14px',
                                                            lineHeight: '1.4',
                                                            marginBottom: '4px',
                                                            whiteSpace: 'pre-wrap'
                                                        }}>
                                                            {message.message_text || message.message}
                                                        </div>
                                                        
                                                        {/* Feedback Rating Display */}
                                                        {isAI && message.feedback_type && (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                marginTop: '6px',
                                                                padding: '4px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                backgroundColor: message.feedback_type === 'helpful' 
                                                                    ? 'rgba(34, 197, 94, 0.15)' 
                                                                    : message.feedback_type === 'not_helpful'
                                                                    ? 'rgba(245, 158, 11, 0.15)'
                                                                    : 'rgba(239, 68, 68, 0.15)',
                                                                color: message.feedback_type === 'helpful'
                                                                    ? '#22c55e'
                                                                    : message.feedback_type === 'not_helpful'
                                                                    ? '#f59e0b'
                                                                    : '#ef4444',
                                                                border: `1px solid ${
                                                                    message.feedback_type === 'helpful' 
                                                                    ? 'rgba(34, 197, 94, 0.3)' 
                                                                    : message.feedback_type === 'not_helpful'
                                                                    ? 'rgba(245, 158, 11, 0.3)'
                                                                    : 'rgba(239, 68, 68, 0.3)'
                                                                }`
                                                            }}>
                                                                <span>{message.feedback_type === 'helpful' ? '👍' : message.feedback_type === 'not_helpful' ? '👎' : '❌'}</span>
                                                                <span>{message.feedback_type === 'helpful' ? 'مفيد' : message.feedback_type === 'not_helpful' ? 'غير مفيد' : 'خطأ'}</span>
                                                            </div>
                                                        )}

                                                        <div style={{
                                                            fontSize: '10px',
                                                            color: isAgent ? 'rgba(255,255,255,0.7)' : '#667781',
                                                            textAlign: 'right',
                                                            marginTop: '2px'
                                                        }}>
                                                            {new Date(message.timestamp).toLocaleTimeString('ar-EG', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                            {isAgent && <span style={{ marginLeft: '4px' }}>✔✔</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </>
                                )}
                            </div>

                            {/* Canned Responses Dropdown */}
                            {showCannedResponses && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '80px',
                                    left: '15px',
                                    right: '15px',
                                    backgroundColor: 'rgba(26, 32, 56, 0.98)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 100
                                }}>
                                    {filteredCannedResponses.map(response => (
                                        <div
                                            key={response.id}
                                            onClick={() => insertCannedResponse(response)}
                                            style={{
                                                padding: '10px 15px',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div style={{
                                                fontWeight: 'bold',
                                                marginBottom: '5px',
                                                color: '#f9fafb',
                                                fontSize: '13px'
                                            }}>
                                                {response.title}
                                                <span style={{
                                                    marginLeft: '10px',
                                                    fontSize: '11px',
                                                    color: '#9ca3af'
                                                }}>
                                                    /{response.shortcuts}
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#d1d5db'
                                            }}>
                                                {response.content_en || response.content_ar}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Message Input */}
                            <div style={{
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                paddingTop: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        ref={messageInputRef}
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !sendingMessage && newMessage.trim()) {
                                                sendMessage();
                                            }
                                        }}
                                        placeholder="Type a message..."
                                        disabled={sendingMessage}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            backgroundColor: '#1a2038',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '24px',
                                            color: '#f9fafb',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={sendingMessage || !newMessage.trim()}
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '50%',
                                            background: sendingMessage || !newMessage.trim() 
                                                ? '#374151' 
                                                : 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '20px',
                                            cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'transform 0.2s',
                                            flexShrink: 0
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!sendingMessage && newMessage.trim()) {
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        {sendingMessage ? '⏳' : '➤'}
                                    </button>
                                </div>
                                
                                <div style={{
                                    marginTop: '8px',
                                    fontSize: '11px',
                                    color: '#9ca3af'
                                }}>
                                    💡 Type "/" for canned responses
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CSS Styles */}
            <style jsx>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                @keyframes slideOut {
                    from {
                        transform: translateX(0);
                        opacity: 1;
                    }
                    to {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                }
                
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
                
                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                /* Desktop - Grid with 2 columns */
                @media (min-width: 768px) {
                    .assignments-list {
                        display: block !important;
                    }
                    
                    div:has(.assignments-list) {
                        grid-template-columns: 1fr 2fr !important;
                    }
                    
                    .mobile-back-button {
                        display: none !important;
                    }
                }

                /* Mobile - Stack vertically */
                @media (max-width: 767px) {
                    .mobile-back-button {
                        display: block !important;
                    }
                    
                    .conversation-view {
                        position: fixed !important;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        z-index: 1000;
                        border-radius: 0 !important;
                        margin: 0 !important;
                        height: 100vh !important;
                    }
                }
            `}</style>
        </div>
    </Layout>
);
};

export default MyAssignments;

